"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

const footerLinks = {
  products: [
    { label: 'Ride Hailing Platform', href: '/products/ride-hailing' },
    { label: 'Logistics Platform', href: '/products/logistics' },
    { label: 'Facility Pro', href: '/products/facility-pro' },
    { label: 'Delivery Services', href: '/products/courier' },
    { label: 'AI Based Call Center', href: '/products/ai-call-center' },
    { label: 'E-Commerce & Retail', href: '/products/ecommerce' },
    { label: 'Ellan CPaaS', href: '/products/ellan-cpaas' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'Careers', href: '/careers' },
    { label: 'Blog', href: '/blog' },
    { label: 'Pricing', href: '/pricing' },
     { label: 'Testimonials', href: '/homepage#testimonials' },
    { label: 'Contact', href: '/contact' },
    { label: 'Dashboard', href: '/dashboard-preview' },
  ],
  resources: [
    { label: 'Book a Demo', href: '/demo-booking' },
    { label: 'Compare Plans', href: '/compare' },
    { label: 'FAQ', href: '/homepage#faq' },
    // { label: 'Terms of Service', href: '#' },
    { label: 'Sitemap', href: '/sitemap.xml' },
  ],
};

const trustBadges = [
  { icon: 'ShieldCheckIcon', label: 'ISO Certified' },
  { icon: 'LockClosedIcon', label: 'Data Secure' },
  { icon: 'CheckBadgeIcon', label: 'GDPR Ready' },
  { icon: 'StarIcon', label: '4.8/5 Rating' },
];

type SocialIconProps = { className?: string };

const WebsiteIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a13.5 13.5 0 013 9 13.5 13.5 0 01-3 9 13.5 13.5 0 01-3-9 13.5 13.5 0 013-9z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InstagramIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" />
  </svg>
);

const ThreadsIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.2 2.5c-4.9 0-8.1 3-8.1 9.5s3.2 9.5 8.1 9.5c4 0 6.4-2 6.9-5.2.5-3.2-1-5.4-4.3-6-2.3-.4-3.9.2-4.4 1.6-.4 1.1.2 2.1 1.6 2.4 1 .2 1.8-.1 2.1-.9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FacebookIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.5 8.5h2V5.3c-.35-.05-1.5-.15-2.85-.15-2.82 0-4.75 1.72-4.75 4.9V13H6v3.6h3.4V22h3.7v-5.4h3.4L17 13h-3.9v-2.5c0-1.04.3-1.75 1.9-1.75z"
      fill="currentColor"
    />
  </svg>
);

const LinkedInIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7.5 10v6.5M7.5 7.6v.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path
      d="M11.2 16.5V13c0-1.4.9-2.3 2.1-2.3s1.9.8 1.9 2.3v3.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M11.2 10.6v5.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const XIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 4l16 16M20 4L4 20"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const YouTubeIcon = ({ className }: SocialIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="6" width="19" height="12" rx="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10.5 9.5l5 2.5-5 2.5v-5z" fill="currentColor" />
  </svg>
);

// Tooltip component with office blue (#0066FF)
const SocialTooltip = ({ label }: { label: string }) => {
  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shadow-lg z-50 pointer-events-none">
      {label}
      <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45" />
    </div>
  );
};

// Social icon with tooltip - reusable component
const SocialIconWithTooltip = ({ 
  icon: IconComponent, 
  label, 
  href 
}: { 
  icon: React.ComponentType<SocialIconProps>, 
  label: string, 
  href: string 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div 
      className="relative inline-flex"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
        aria-label={label}
      >
        <IconComponent className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
      </a>
      
      {showTooltip && (
        <div 
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shadow-lg z-50 pointer-events-none"
          style={{ 
            background: '#0066FF',
            color: 'white'
          }}
        >
          {label === 'Instagram' ? 'Instagram' : label}
          <div 
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45"
            style={{ background: '#0066FF' }}
          />
        </div>
      )}
    </div>
  );
};

const socialGroups = [
  {
    title: 'Follow Us',
    links: [
      { icon: InstagramIcon, label: 'Instagram', href: 'https://www.instagram.com/moviohq/' },
      { icon: FacebookIcon, label: 'Facebook', href: 'https://www.facebook.com/moviohq' },
      { icon: YouTubeIcon, label: 'YouTube', href: 'https://www.youtube.com/@Movio-hq' },
      { icon: ThreadsIcon, label: 'Threads', href: 'https://www.threads.com/@moviohq/' },
    ],
  },
  {
    title: 'Connect With Us',
    links: [
      { icon: LinkedInIcon, label: 'LinkedIn', href: 'https://www.linkedin.com/company/moviohq' },
      { icon: XIcon, label: 'X', href: 'https://x.com/movioai' },
    ],
  },
  {
    title: 'Visit Us',
    links: [
      { icon: WebsiteIcon, label: 'movio.in', href: 'https://movio.in' },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      role="contentinfo"
      aria-label="Site footer"
      className="w-full relative z-10"
      style={{ background: 'linear-gradient(160deg, #075DDF 0%, #0066FF 55%, #075DDF 100%)' }}
    >
      {/* Trust badges bar */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 lg:gap-10">
            {trustBadges.map((badge) => (
              <div key={badge.label} className="flex items-center gap-1.5 md:gap-2">
                <Icon name={badge.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-white" />
                <span className="text-[10px] md:text-xs font-medium text-white/90">{badge.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 md:gap-2">
              <span className="relative w-1.5 h-1.5 md:w-2 md:h-2 inline-flex">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-full w-full bg-emerald-400" />
              </span>
              <span className="text-[10px] md:text-xs text-emerald-300 font-medium">All systems operational</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 lg:py-10">
        {/* Top section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-8 lg:gap-10 mb-4 md:mb-8">

          {/* Brand & Description */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="inline-flex items-center bg-white/95 px-3 py-1.5 rounded-xl shadow-md mb-3 md:mb-4">
              <Image
                src="/assets/images/movio-logo.png"
                alt="Movio"
                width={120}
                height={37}
                className="object-contain"
              />
            </div>
            <p className="text-xs md:text-sm text-white/85 leading-relaxed mb-2 md:mb-3">
              India's Mobility Operating System. Empowering local taxi, logistics, delivery, and service operators to launch, scale, and grow their own digital businesses—under their own brand.
            </p>
            <p className="text-xs md:text-sm text-white/90 font-medium italic mb-4 md:mb-6">
              Your Brand. Your Business. Our Technology.
            </p>

            {/* Contact Info */}
            <div className="flex flex-col gap-2 md:gap-3 mb-4 md:mb-6">
              <a
                href="mailto:contact@movio.in"
                className="flex items-center gap-2 md:gap-2.5 text-xs md:text-sm text-white/85 hover:text-white transition-colors group"
              >
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg flex items-center justify-center bg-white/20 group-hover:bg-white/30 transition-colors flex-shrink-0">
                  <Icon name="EnvelopeIcon" size={12} className="text-white" />
                </div>
                contact@movio.in
              </a>
              <a
                href="tel:+919600830488"
                className="flex items-center gap-2 md:gap-2.5 text-xs md:text-sm text-white/85 hover:text-white transition-colors group"
              >
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg flex items-center justify-center bg-white/20 group-hover:bg-white/30 transition-colors flex-shrink-0">
                  <Icon name="PhoneIcon" size={12} className="text-white" />
                </div>
                +91 9600830488
              </a>
              <div className="flex items-start gap-2 md:gap-2.5">
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg flex items-center justify-center bg-white/20 flex-shrink-0 mt-0.5">
                  <Icon name="MapPinIcon" size={12} className="text-white" />
                </div>
                <span className="text-xs md:text-sm text-white/85">Tamil Nadu, India</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-semibold text-white text-xs md:text-sm mb-3 md:mb-4 uppercase tracking-wider">Products</h3>
            <nav className="flex flex-col gap-2 md:gap-2.5" aria-label="Products navigation">
              {footerLinks.products.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-xs md:text-sm text-white/85 hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-white text-xs md:text-sm mb-3 md:mb-4 uppercase tracking-wider">Company</h3>
            <nav className="flex flex-col gap-2 md:gap-2.5" aria-label="Company navigation">
              {footerLinks.company.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-xs md:text-sm text-white/85 hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-white text-xs md:text-sm mb-3 md:mb-4 uppercase tracking-wider">Resources</h3>
            <nav className="flex flex-col gap-2 md:gap-2.5" aria-label="Resources navigation">
              {footerLinks.resources.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-xs md:text-sm text-white/85 hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="mt-4 md:mt-6">
              <h3 className="font-semibold text-white text-xs md:text-sm mb-2 md:mb-3 uppercase tracking-wider">Active States</h3>
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {['Tamil Nadu', 'Kerala', 'Karnataka', 'Chhattisgarh'].map((state) => (
                  <span
                    key={state}
                    className="text-[10px] md:text-xs px-2 py-0.5 md:px-2.5 md:py-1 rounded-full bg-white/15 border border-white/25 text-white/80 font-medium"
                  >
                    {state}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Social + Subscribe Banner */}
        <div className="border-t border-white/10 py-3 md:py-6 mb-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-8">

            {/* Left - Social with tooltips */}
            <div className="flex flex-wrap gap-4 md:gap-6 lg:gap-12">
              {socialGroups.map((group) => (
                <div key={group.title}>
                  <h3 className="font-semibold text-white text-xs md:text-sm mb-2 md:mb-3 uppercase tracking-wider">
                    {group.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 md:gap-3">
                    {group.links.map((link) => (
                      <SocialIconWithTooltip
                        key={link.label}
                        icon={link.icon}
                        label={link.label}
                        href={link.href}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Right - Subscribe Banner */}
            <div className="lg:w-[620px] w-full">
              <div className="bg-white/10 border border-white/20 rounded-xl md:rounded-2xl px-4 md:px-6 py-4 md:py-5 backdrop-blur-md">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 md:gap-5">
                  <div>
                    <h3 className="text-white text-base md:text-xl font-semibold">
                      Stay updated on mobility trends
                    </h3>
                    <p className="text-white/80 text-xs md:text-sm mt-1 md:mt-2">
                      Get insights on fleet tech, delivery platforms, and operator success stories.
                    </p>
                  </div>
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-white text-[#0066FF] px-4 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold hover:bg-white/90 transition-colors text-sm md:text-base"
                  >
                    Subscribe to Updates
                    <Icon name="ArrowRightIcon" size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-2 md:pt-3">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">

            {/* Left */}
            <div className="flex flex-col text-center lg:text-left">
              <p className="text-[10px] md:text-xs text-white/85">
                © 2026 Movio. All rights reserved.
              </p>
              <p className="text-[10px] md:text-xs text-white/60">
                Movio is a brand owned and operated by Ellan Mobility Private Limited.
              </p>
            </div>

            {/* Center */}
            <div className="hidden lg:block">
              <p className="text-xs text-white/70 font-medium tracking-wide">
                India's Mobility Operating System
              </p>
            </div>

            {/* Right */}
<div className="flex items-center gap-3 md:gap-4">
  <span className="text-[10px] md:text-xs text-white/85">
    22+ Cities Served
  </span>

  <span className="text-white/60">|</span>

  <span className="text-[10px] md:text-xs text-white/85">
    Tier 2 & Tier 3 Focused
  </span>

  <span className="text-white/60">|</span>

  <span className="text-[10px] md:text-xs text-white/85">
    Made in India 🇮🇳
  </span>
</div>

          </div>
        </div>
      </div>
    </footer>
  );
}